<template>
  <div
    class="jps-validation-field jps-field"
    :class="{ 'is-invalid': !isValid }"
    v-if="init"
  >
    <span
      class="jps-label"
      :class="{ 'multi-line-margin': ruleField.allowMultiline }"
    >
      {{ label }}
      <span class="allow-html" v-if="ruleField.allowHTML">{{ ls({ ja: 'HTML可', en: '(HTML allowed)' }) }}</span>
      <UiInformationTextTooltip :text="ruleField.description" />
    </span>
    <template v-if="isArray">
      <!--複数の値を持つフィールドの場合-->
      <!-- labelのフォーカスを引き受けるダミーボタン -->
      <div class="jps-tag-list">
        <!--表示-->
        <span
          class="jps-tag jps-dark jps-slim jps-tag-ricon"
          v-for="(item, index) in value"
          :key="index"
        >
          <template v-if="isTag">{{ ls(item.label) }}</template>
          <template v-else-if="isCode">{{ lc(code, item) }}</template>
          <!-- <template v-else-if="isEnum">{{ lcdesc(code, item) }}</template> TODO バグ調査 ※今回の対応前から存在したTODO-->
          <template v-else-if="isLangString">{{ ls(item) }}</template>
          <template v-else>{{ item }}</template>
          <button
              class="jps-icon-button jps-grey jps-slim is-size-6"
              @click="removeFromArray(value, item)"
          >
            <BaseIcon name="mdi/close-circle" />
          </button>
        </span>
        <button type="button" class="jps-tag jps-form" style="height: 24px;" @click.stop="addValue()">
          <BaseIcon size="size-5" name="mdi/plus" />
        </button>
        <TagSelectorModal ref="tagSelectorModal" />
        <ValidationSelectModal
          ref="validationSelectModal"
          :selected="value || []"
          :rule="rule"
          :ruleField="ruleField"
        />
      </div>
    </template>
    <template v-else-if="isImageSelection">
      <div>
        <UiImageSelection :image="value" />
        <button type="button" class="jps-button" @click="selectImage()">
          {{ ls({ ja: '画像の選択', en: 'Select Image' }) }}
        </button>
        <button
          :disabled="value == null"
          type="button"
          class="jps-button"
          @click="value = null"
        >
          {{ ls(dapLangGlobal.unselect) }}
        </button>
        <ImageSelectModal
          ref="imageSelectModal"
          :image="value"
          :rule-field="ruleField"
        />
      </div>
    </template>
    <template v-else-if="isLangString">
      <LangStringEditor :rule="ruleField" :value="value" noplaceholder="" />
    </template>
    <template v-else-if="isDate">
      <BaseDatePicker v-model="value" />
    </template>
    <template v-else-if="isObject"></template>
    <template v-else>
      <!--単一の値のフィールドの場合-->
      <input
        v-if="isText && !isMultiline"
        type="text"
        class="jps-input"
        v-model="value"
        :disabled="disabled" />
      <textarea
        v-if="isText && isMultiline"
        class="jps-textarea"
        v-model="value"
        :disabled="disabled"
        rows="3"
      ></textarea>

      <BaseToggleSwitch v-if="isCheckbox" v-model="value" />
      <input
        v-if="isNumber"
        class="jps-input"
        type="number"
        v-model.number="value"
        :min="ruleField.numMin"
        :max="ruleField.numMax"
        :disabled="disabled" />
      <BaseSelect
        v-if="isCode && codeKeys"
        class="jps-select"
        :code="code"
        :mandatory="ruleField.mandatory"
        v-model="value"
        :disabled="disabled"
      />
      <BaseSelect
        v-if="isEnum"
        class="jps-select"
        :enum="enumValue"
        v-model="value"
        :disabled="disabled"
      />
    </template>
    <div class="lower-info">
      <span class="input-error" v-if="error">{{ error }}</span>
      <span class="input-help" v-if="!disabled && ruleField.inputHelp">
        {{ ls(ruleField.inputHelp) }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { TagSelectorModal, ValidationSelectModal, ImageSelectModal } from '#components'

const mandatory = { ja: '必須項目です', en: 'Mandatory field' }

const { ls, lst, lss, lc, lcdesc } = useI18n()

interface Props {
  rule: ValidationRule,
  ruleField: ValidationRuleField,
  target?: any,
  simple?: boolean,
  disabled?: boolean,
  validation?: any,
}
const props = withDefaults(defineProps<Props>(),{})

const emit = defineEmits(['valid-change'])

const init: Ref<boolean> = ref(false)
const {
  label,
  isImageSelection,
  isArray,
  isLangString,
  isTag,
  isObject,
  isText,
  isMultiline,
  isCheckbox,
  isNumber,
  isCode,
  code,
  codeKeys,
  isEnum,
  enumValue,
  isDate,
} = useValidation(props.rule, props.ruleField)

const value = computed({
  get: (): any => {
    if (props.ruleField.field in props.target) {
      return props.target[props.ruleField.field]
    } else {
      return null
    }
  },
  set: v => {
    props.target[props.ruleField.field] = v
  },
})

const error = computed(() => {
  if (isArray.value) {
    let arrayCount = 0
    if (value.value) {
      arrayCount = value.value.length
    }
    let minCount = props.ruleField.arrayMin
    if (props.ruleField.mandatory && !minCount) {
      minCount = 1
    }
    if (props.ruleField.arrayMax && arrayCount > props.ruleField.arrayMax) {
      return lst(
        {
          ja: '{}つ以上は選択できません',
          en: 'Select no more than {}',
        },
        props.ruleField.arrayMax
      )
    }
    if (minCount && arrayCount < minCount) {
      return lst(
        {
          ja: '{}つ以上選択してください',
          en: 'Select at least {}',
        },
        minCount
      )
    }
  } else if (isImageSelection.value) {
    if (props.ruleField.mandatory && value.value == null)
      return ls(mandatory)
  } else if (isLangString.value) {
    let t = []
    if (
      props.ruleField.jaMandatory &&
      (value.value == null || isEmptyString(value.value.ja))
    )
      t.push(lss('日本語', 'Japanese '))
    if (
      props.ruleField.enMandatory &&
      (value.value == null || isEmptyString(value.value.en))
    )
      t.push(lss('英語', 'English '))
    if (
        props.ruleField.yomiMandatory &&
      (value.value == null || !value.value.yomi)
    )
      t.push(lss('読み', 'Reading'))
    if (t.length > 0)
      return t.join('/') + lss('は必須です', ' is mandatory')
  } else if (isText.value) {
    if (props.ruleField.mandatory && isEmptyString(value.value))
      return ls(mandatory)
    return checkValue(value.value)
  } else {
    if (props.ruleField.mandatory && (value.value == null || value.value === ''))
      return ls(mandatory)
    return checkValue(value.value)
  }
  return null
})

const checkValue = (targetValue): string => {
  if (isText.value) {
    if (
        props.ruleField.strPattern &&
        value.value &&
        !(value.value as string).match('^' + props.ruleField.strPattern + '$')
    )
      return ls({
        ja: '入力ルールに一致しません',
        en: 'Contradict to Input Rule',
      })
  } else if (isNumber.value) {
    const num = Number(value.value)
    if (Number.isNaN(num))
      return ls({ ja: '数字を入力して下さい', en: 'Input number' })
    if (props.ruleField.numIntOnly && !Number.isInteger(num))
      return ls({
        ja: '整数のみ許可されています',
        en: 'fraction is not allowed',
      })
    if (value.value != null && props.ruleField.numMax < targetValue)
      return lst(
        { ja: '最大値は{}です', en: '{} is max' },
        props.ruleField.numMax
      )
    if (value.value != null && props.ruleField.numMin > targetValue)
      return lst(
        { ja: '最小値は{}です', en: '{} is minimum' },
        props.ruleField.numMin
      )
  }
  return null
}

const watchError = () => {
  props.validation[props.ruleField.field] = isValid.value
  emit('valid-change')
}
watch(
  () => error.value,
  () => watchError(),
)

const isValid = computed(() => {
  return !error.value
})

const tagSelectorModal = ref<InstanceType<typeof TagSelectorModal>>()
const validationSelectModal = ref<InstanceType<typeof ValidationSelectModal>>()
const addValue = async () => {
  if (isTag.value) {
    const tag = await tagSelectorModal.value.openAwait()
    value.value = value.value ? [...value.value, tag] : [tag]
  } else {
    const added = await validationSelectModal.value.openAwait()
    value.value = value.value ? [...value.value, added] : [added]
  }
}

const imageSelectModal = ref<InstanceType<typeof ImageSelectModal>>()
const selectImage = async () => {
  try {
    value.value = await imageSelectModal.value.openAwait()
  } catch (cancel) {}
}

// TODO disableが効かないフィールドを修正する ※今回の対応前から存在したTODO
onBeforeMount(() => {
  //targetのフィールドに値が無い場合、Nullをセット
  if (props.target[props.ruleField.field] === undefined) {
    if (isObject.value) {
      props.target[props.ruleField.field] = {}
    } else if (isLangString.value) {
      props.target[props.ruleField.field] = {ja: '', en: ''}
    } else {
      props.target[props.ruleField.field] = null
    }
  }
  watchError()
  init.value = true
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-validation-field {
  .tag-button {
    height: 32px;
    padding-top: 0px;
    padding-bottom: 0px;
    /*        align-items: center;
        display: inline-flex;*/
  }

  .jps-select {
    margin-left: 0;
  }
  .jps-tag-list {
    margin: 0;
  }

  .jps-image-selection {
    /* width: 200px; */
    height: 200px;
  }
  .color-span {
    width: 2rem;
    height: 1rem;
  }
  .allow-html {
    color: $jps-gray-500;
    font-size: 0.875rem;
    margin-left: 0.5rem;
  }

  .lower-info {
    font-size: 0.75rem;
    .input-error {
      color: $jps-key-red;
    }
    > *:not(:first-child) {
      padding-left: 0.25rem;
    }
  }
}
</style>
